<script lang="ts">
/**
 * A dropdown menu
 * ```vue
 * <Menu>
 *   <template #activator>Open menu</template>
 *   <MenuItem>Item 1</MenuItem>
 *   <MenuItem>Item 2</MenuItem>
 *   <SubMenu>
 *      <template #activator>Open Sub Menu</template>
 *      <MenuItem>SubMenu 1</MenuItem>
 *   <SubMenu>
 *   <MenuRadioGroup>
 *     <MenuRadioItem value="first">First</MenuRadioItem>
 *     <MenuRadioItem value="second">Second</MenuRadioItem>
 *   </MenuRadioGroup>
 *   <MenuCheckboxItem v-model="checked">Toggle me</MenuCheckboxItem>
 * ```
 */
export default {};

export interface MenuProps extends ClassProps, PlacementProps, DeprecatedPlacementProps {
	matchWidth?: boolean;
	open?: boolean;
	offset?: number;
	disabled?: boolean;
}
</script>

<script setup lang="ts">
import {
	DropdownMenuContent,
	DropdownMenuPortal,
	DropdownMenuRoot,
	DropdownMenuTrigger,
	useForwardPropsEmits,
} from "reka-ui";
import { computed } from "vue";
import type { ClassProps } from "../../lib/base-props";
import {
	convertDeprecatedPlacement,
	type DeprecatedPlacementProps,
	type PlacementProps,
} from "../../lib/placement";
import { cn } from "../../lib/utils";

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<MenuProps>(), { offset: 4 });

const emit = defineEmits<{
	(evt: "update:open", val: boolean): void;
}>();

defineSlots<{
	default(args: { open: boolean }): void;
	trigger(args: { open: boolean }): void;

	/** @deprecated */
	activator(args: { open: boolean }): void;
}>();

const placement = computed(() => {
	if (props.placement != null) {
		return convertDeprecatedPlacement(props.placement);
	}

	return { side: props.side, align: props.align };
});

const forwarded = useForwardPropsEmits(props, emit);
</script>

<template>
	<DropdownMenuRoot v-slot="slotProps" v-bind="forwarded" :modal="false">
		<DropdownMenuTrigger asChild :disabled="disabled">
			<slot name="trigger" v-bind="slotProps">
				<slot name="activator" v-bind="slotProps" />
			</slot>
		</DropdownMenuTrigger>

		<DropdownMenuPortal>
			<DropdownMenuContent
				as="ul"
				:class="
					cn(
						{
							'menu popover popover-animate gap-1': true,
							'min-w-32': !matchWidth,
							'menu-match-width': matchWidth,
						},
						props.class
					)
				"
				:sideOffset="offset"
				:side="placement.side"
				:align="placement.align"
				v-bind="$attrs"
			>
				<slot v-bind="slotProps" />
			</DropdownMenuContent>
		</DropdownMenuPortal>
	</DropdownMenuRoot>
</template>
